import yieldToTaskQueue from './yieldToTaskQueue';

// ----------------------------------------------------------------------------

export type TypeTimeSlice = {
    /*
     * Call it as often as is convenient — after each item of a loop, say. It
     * returns without doing anything until the slice is spent, so the cost of
     * asking is a clock read.
     */
    maybeYield: () => Promise<void>;
};

// ----------------------------------------------------------------------------

/*
 * Caps how long a loop may hold the main thread before letting go of it.
 *
 * By time and not by item count: a count cannot know what an item costs, so it
 * either yields far more often than the work warrants — paying for a pause every
 * fifty items whether they took a microsecond or a second — or it lets a slow
 * batch of items block for as long as it likes. The slice is the longest the loop
 * can block, whatever its items turn out to cost, which is the guarantee a caller
 * actually wants: eight milliseconds fits inside half a frame at 60Hz.
 *
 * A slice of zero switches the yielding off, for a caller that has no thread to
 * be polite to.
 */
function createTimeSlice(everyMs: number): TypeTimeSlice {
    let lastYieldAt = performance.now();

    const maybeYield = async function (): Promise<void> {
        if (everyMs <= 0) {
            return;
        }

        if (performance.now() - lastYieldAt < everyMs) {
            return;
        }

        await yieldToTaskQueue();

        // eslint-disable-next-line require-atomic-updates -- callers sharing a slice share its budget by design; the last writer wins, which is the reading every one of them wants
        lastYieldAt = performance.now();
    };

    return { maybeYield };
}

// ----------------------------------------------------------------------------

export default createTimeSlice;
